import * as Sentry from '@sentry/react';
import {
  useMutation,
  useQueries,
  type QueryObserverResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { createInitiative } from '../../api/initiative-client/initiative-client';
import { useInitiatives, useKPIOverviewData } from '../../api/initiative-client/initiative-client.hooks';
import type {
  ExtendedInitiative,
  Initiative,
  KPIOverviewData,
  PreSaveInitiative,
} from '../../api/initiative-client/initiative-client.type';
import { InitiativeStatus } from '../../api/initiative-client/initiative-client.type';
import type { Portfolio } from '../../api/portfolio-client/portfolio-client.type';
import { useEpics } from '../../api/process-client/process-client.hooks';
import { createGoal, createOutcome } from '../../api/strategy-client/strategy-client';
import { useGoals, useOutcomes } from '../../api/strategy-client/strategy-client.hooks';
import type { Goal, Outcome, PreSaveGoal, PreSaveOutcome } from '../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../api/tasks-client/task-client.type';
import { getWorkPeriodsMeasure } from '../../api/work-periods-client/work-periods-client';
import type {
  GetWorkPeriodsMeasurePayload,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../api/work-periods-client/work-periods-client.type';
import { TimeAllocationType, Transformer } from '../../api/work-periods-client/work-periods-client.type';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useStrategyStore } from '../../store/strategy-store/strategy-store';
import {
  setEpics,
  setInitiatives,
  setKPIOverviewData,
  setSelectedInitiative,
} from '../../store/strategy-store/strategy-store.actions';
import { timeAllocationTransformerMeasures } from '../../helpers/process-analysis-shared/process-analysis-shared.hooks';
import { getContributingBoards, getContributingTeams } from './initiative-list/main-section/main-section.helpers';
import { filterInitiativesBySearchTerm } from './strategy.helpers';
import { InitiativeRecord, InitiativeState, Status, StrategyTab } from './strategy.type';

/**
 * Hook to fetch outcomes data
 */
export function useOutcomesQuery(options?: UseQueryOptions<Outcome[]>) {
  return useOutcomes(options);
}

/**
 * Hook to fetch goals data
 */
export function useGoalsQuery(options?: UseQueryOptions<Goal[]>) {
  return useGoals(options);
}

/**
 * Hook to create a new initiative mutation
 *
 * @param portfolio - The portfolio to create the initiative in, or null if no portfolio is selected
 * @param options - Optional mutation options to customize behavior
 * @returns A mutation result object containing the mutation function and state
 * @throws Error if no portfolio is selected when attempting to create
 */
const useCreateInitiativeMutation = (
  portfolio: Portfolio | null,
  options?: Partial<UseMutationOptions<Initiative, Error, PreSaveInitiative>>,
): UseMutationResult<Initiative, Error, PreSaveInitiative> => {
  return useMutation({
    mutationKey: ['createInitiative', portfolio],
    mutationFn: (initiative: PreSaveInitiative) =>
      portfolio ? createInitiative(portfolio.id, initiative) : Promise.reject('No portfolio selected'),
    ...options,
  } as UseMutationOptions<Initiative, Error, PreSaveInitiative>);
};

/**
 * Hook to create a new outcome mutation
 *
 * @param options - Optional mutation options to customize behavior
 * @returns A mutation result object containing the mutation function and state
 */
const useCreateOutcomeMutation = (
  options?: Partial<UseMutationOptions<Outcome, Error, PreSaveOutcome>>,
): UseMutationResult<Outcome, Error, PreSaveOutcome> => {
  return useMutation({
    mutationKey: ['createOutcome'],
    mutationFn: (outcome: PreSaveOutcome) => createOutcome(outcome),
    ...options,
  } as UseMutationOptions<Outcome, Error, PreSaveOutcome>);
};

/**
 * Hook to create a new goal mutation
 *
 * @param options - Optional mutation options to customize behavior
 * @returns A mutation result object containing the mutation function and state
 */
const useCreateGoalMutation = (
  options?: Partial<UseMutationOptions<Goal, Error, PreSaveGoal>>,
): UseMutationResult<Goal, Error, PreSaveGoal> => {
  return useMutation({
    mutationKey: ['createGoal'],
    mutationFn: (goal: PreSaveGoal) => createGoal(goal),
    ...options,
  } as UseMutationOptions<Goal, Error, PreSaveGoal>);
};

/**
 * Custom hook that fetches and manages strategy data
 *
 * This hook fetches initiatives and KPI overview data for the current portfolio,
 * updates the global store with the fetched data, and handles error cases.
 *
 * @returns {Object} Object containing query results and data
 * @returns {QueryObserverResult<Initiative[]>} initiativesQuery - Query result for initiatives
 * @returns {Initiative[]} initiatives - Array of initiatives
 * @returns {QueryObserverResult<KPIOverviewData>} kpiOverviewDataQuery - Query result for KPI overview data
 * @returns {KPIOverviewData | null} kpiOverviewData - KPI overview data or null if not available
 */
const useStrategyData = (): {
  initiativesQuery: QueryObserverResult<Initiative[]>;
  initiatives: Initiative[];
  kpiOverviewDataQuery: QueryObserverResult<KPIOverviewData>;
  kpiOverviewData: KPIOverviewData | null;
} => {
  const { initiativesQuery, initiatives } = useInitiativesQuery();
  const { kpiOverviewDataQuery, kpiOverviewData } = useKPIOverviewDataQuery();

  return {
    initiativesQuery,
    initiatives,
    kpiOverviewDataQuery,
    kpiOverviewData,
  };
};

/**
 * Custom hook that provides initiatives data and query results
 *
 * This hook fetches initiatives for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @returns {Object} Object containing query results and initiatives data
 * @returns {QueryObserverResult<Initiative[]>} initiativesQuery - Query result for initiatives
 * @returns {Initiative[]} initiatives - Array of initiatives
 */
const useInitiativesQuery = (): {
  initiativesQuery: QueryObserverResult<Initiative[]>;
  initiatives: Initiative[];
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedInitiative = useStrategyStore((state) => state.selectedInitiative);

  const { query: initiativesQuery, initiatives = [] } = useInitiatives(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<Initiative[]>);

  useEffect(() => {
    if (initiativesQuery.isSuccess) {
      setInitiatives(initiatives);

      if (selectedInitiative) {
        const initiative = initiatives.find((initiative) => initiative.id === selectedInitiative.id);

        if (initiative) {
          setSelectedInitiative(initiative);
        }
      }
    }
  }, [initiatives, initiativesQuery.isSuccess, selectedInitiative]);

  useEffect(() => {
    if (initiativesQuery.error) {
      setInitiatives([]);
      Sentry.captureException(initiativesQuery.error);
    }
  }, [initiativesQuery.error]);

  return { initiativesQuery, initiatives };
};

/**
 * Custom hook that provides KPI overview data and query results
 *
 * This hook fetches KPI overview data for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @returns {Object} Object containing query results and KPI overview data
 * @returns {QueryObserverResult<KPIOverviewData>} kpiOverviewDataQuery - Query result for KPI overview data
 * @returns {KPIOverviewData | null} kpiOverviewData - KPI overview data or null if not available
 */
const useKPIOverviewDataQuery = (): {
  kpiOverviewDataQuery: QueryObserverResult<KPIOverviewData>;
  kpiOverviewData: KPIOverviewData | null;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const { query: kpiOverviewDataQuery, kpiOverviewData = null } = useKPIOverviewData(portfolio?.id, {
    enabled: !!portfolio?.id,
  } as UseQueryOptions<KPIOverviewData>);

  useEffect(() => {
    if (kpiOverviewDataQuery.isSuccess) {
      setKPIOverviewData(kpiOverviewData ?? null);
    }
  }, [kpiOverviewData, kpiOverviewDataQuery.isSuccess]);

  useEffect(() => {
    if (kpiOverviewDataQuery.error) {
      setKPIOverviewData(null);
      Sentry.captureException(kpiOverviewDataQuery.error);
    }
  }, [kpiOverviewDataQuery.error]);

  return { kpiOverviewDataQuery, kpiOverviewData };
};

/**
 * Custom hook that provides epics data and query results
 *
 * This hook fetches epics for the current portfolio and handles
 * success and error states, updating the global store accordingly.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enabled=false] - Whether to enable the query
 * @returns {Object} Object containing query results and epics data
 * @returns {QueryObserverResult<Epic[]>} epicsQuery - Query result for epics
 * @returns {Epic[]} epics - Array of epics
 */
const useEpicsQuery = ({
  enabled = false,
}: {
  enabled?: boolean;
} = {}): {
  epicsQuery: QueryObserverResult<Epic[]>;
  epics: Epic[];
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const { query: epicsQuery, epics = [] } = useEpics(
    { portfolioId: portfolio?.id || null, projectId: null, subprojectId: null },
    {
      enabled: !!portfolio?.id && enabled,
    } as UseQueryOptions<Epic[]>,
  );

  useEffect(() => {
    if (epicsQuery.isSuccess) {
      setEpics(epics);
    }
  }, [epics, epicsQuery.isSuccess]);

  useEffect(() => {
    if (epicsQuery.error) {
      setEpics([]);
      Sentry.captureException(epicsQuery.error);
    }
  }, [epicsQuery.error]);

  return { epicsQuery, epics };
};

/**
 * Custom hook that provides filtered initiatives based on search term and filters
 *
 * This hook filters initiatives based on the current search term, source filters,
 * and status filters from the strategy store. When a search term is provided and
 * search is enabled, it uses search results instead of global initiatives.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.enableSearch=true] - Whether to enable search functionality
 * @returns {Object} Object containing filtered initiatives and loading state
 * @returns {Initiative[]} initiatives - Array of filtered initiatives
 */
const useFilteredInitiatives = ({
  enableSearch = true,
}: {
  enableSearch?: boolean;
} = {}): {
  initiatives: InitiativeRecord[];
} => {
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teams = useGlobalStore((state) => state.teams);
  const globalInitiatives = useStrategyStore((state) => state.initiatives);
  const searchTerm = useStrategyStore((state) => state.searchTerm);
  const sourceFilters = useStrategyStore((state) => state.filters.source || []);
  const stateFilters = useStrategyStore((state) => state.filters.state || []);
  const statusFilters = useStrategyStore((state) => state.filters.status || []);
  const isActiveFilters = useStrategyStore((state) => state.filters.is_active || []);
  const onlyBookmarkedFilter = useStrategyStore((state) => state.filters.only_bookmarked || false);
  const parentOutcomesFilters = useStrategyStore((state) => state.filters.parent_outcomes || []);
  const committedNextWeeksEnabled = useStrategyStore((state) => state.filters.committed_next_weeks_enabled || false);
  const committedNextWeeksValue = useStrategyStore((state) => state.filters.committed_next_weeks_value || 4);

  const { outcomes } = useOutcomes();

  const initiatives = useMemo(
    () =>
      searchTerm && enableSearch ? filterInitiativesBySearchTerm(globalInitiatives, searchTerm) : globalInitiatives,
    [globalInitiatives, searchTerm, enableSearch],
  );

  // Filter by source
  let filteredInitiatives = initiatives.filter((initiative) => sourceFilters.includes(initiative.source));

  // Filter by state (no epics and no end date)
  // Filter initiatives with no epics
  if (stateFilters.includes(InitiativeState.NoEpics)) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => initiative.epic_count === 0);
  }

  // Filter initiatives with no end date
  if (stateFilters.includes(InitiativeState.NoEndDate)) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => initiative.no_end_date === true);
  }

  // Filter by status
  if (statusFilters.length > 0) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      if (initiative.status?.by_tasks?.status) {
        return statusFilters.includes(initiative.status?.by_tasks?.status);
      } else {
        if (
          statusFilters.includes(Status.Unknown) &&
          (initiative.status?.by_tasks?.status === InitiativeStatus.Empty || !initiative.status?.by_tasks?.status)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  // Filter by active/inactive state
  if (isActiveFilters.length > 0) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      if (portfolio?.use_end_date) {
        const isActive =
          isActiveFilters.includes(InitiativeState.Active) && dayjs(initiative.end_date).isAfter(dayjs());
        const isInactive =
          isActiveFilters.includes(InitiativeState.Inactive) && dayjs(initiative.end_date).isBefore(dayjs());
        return isActive || isInactive;
      } else {
        const isActive = isActiveFilters.includes(InitiativeState.Active) && initiative.is_active;
        const isInactive = isActiveFilters.includes(InitiativeState.Inactive) && !initiative.is_active;
        return isActive || isInactive;
      }
    });
  }

  // Filter by bookmarked
  if (onlyBookmarkedFilter) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => initiative.is_bookmarked);
  }

  // Filter by committed initiatives in next X weeks
  if (committedNextWeeksEnabled) {
    const today = dayjs().startOf('day');
    const futureDate = today.add(committedNextWeeksValue, 'week');

    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      const targetDate = initiative.end_date;

      if (!targetDate) return false;

      const initiativeDate = dayjs(targetDate);

      return (
        initiativeDate.isSame(today, 'day') ||
        initiativeDate.isSame(futureDate, 'day') ||
        (initiativeDate.isAfter(today) && initiativeDate.isBefore(futureDate))
      );
    });
  }

  // Filter by parent outcomes
  if (parentOutcomesFilters.length > 0) {
    filteredInitiatives = filteredInitiatives.filter((initiative) => {
      const parentOutcomes = outcomes.filter((outcome) =>
        outcome.initiatives?.some((init) => init.id === initiative.id),
      );
      return parentOutcomes.some((outcome) => parentOutcomesFilters.includes(outcome.id));
    });
  }

  const formattedInitiatives = filteredInitiatives.map((initiative) => ({
    ...initiative,
    contributing_teams_label: organization?.prefer_contributing_boards_over_time
      ? getContributingBoards(initiative, teams)
      : getContributingTeams(initiative, teams),
  }));

  return { initiatives: formattedInitiatives };
};

/**
 * Hook to check if any initiative filters are currently applied
 *
 * @returns {boolean} True if any source, state, status or active/inactive filters are selected, false otherwise
 */
const useFiltersApplied = (): boolean => {
  const sourceFilters = useStrategyStore((state) => state.filters.source || []);
  const stateFilters = useStrategyStore((state) => state.filters.state || []);
  const statusFilters = useStrategyStore((state) => state.filters.status || []);
  const isActiveFilters = useStrategyStore((state) => state.filters.is_active || []);

  return sourceFilters.length > 0 || stateFilters.length > 0 || statusFilters.length > 0 || isActiveFilters.length > 0;
};

/**
 * Hook to fetch measure data for a specific initiative
 *
 * @param {Object} params - The params object
 * @param {ExtendedInitiative} params.initiative - The initiative to fetch measure data for
 * @param {MeasureMetadata[]} params.measureMetadata - The measure metadata to fetch data for
 * @returns {Object} Object containing:
 *   - data: The measure data
 *   - isLoading: Whether the measure data is loading
 */
const useInitiativeMeasureData = ({
  initiative,
  measureMetadata,
}: {
  initiative: ExtendedInitiative | null;
  measureMetadata: MeasureMetadata[];
}): { data: (MeasureDataResponse | undefined)[]; isLoading: boolean } => {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const organizationId = useGlobalStore((state) => state.organization?.id);

  const queryConfigs = measureMetadata.map((measure) => {
    const isCustom = measure.is_custom ?? false;
    const payload: GetWorkPeriodsMeasurePayload = {
      start_date: initiative?.start_date ? dayjs(initiative.start_date).format('YYYY-MM-DD') : undefined,
      end_date: initiative?.end_date ? dayjs(initiative.end_date).format('YYYY-MM-DD') : undefined,
      initiative_id: initiative?.id,
      measure_name: [measure.measure_name],
      force_recalculation: false,
      is_custom: isCustom,
      time_allocation_type: TimeAllocationType.BiWeekly,
      organization_id: organizationId || undefined,
    };
    if (isCustom) {
      payload.transformer = Transformer.ReturnFirstMetricOnly;
    } else if (timeAllocationTransformerMeasures.includes(measure.measure_name)) {
      payload.transformer = Transformer.TimeAllocationValuesOnly;
    }
    return {
      queryKey: ['initiative-measure-data', initiative?.id, measure.measure_name, organizationId] as const,
      queryFn: () =>
        getWorkPeriodsMeasure<MeasureDataResponse>({
          ...payload,
          superuser_force_recalculation: superuserMode.enabled ? superuserMode.forceRecalculation : false,
        }),
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
      retry: false,
      enabled: !!organizationId,
    };
  });

  const queries = useQueries({ queries: queryConfigs });
  const isLoading = queries.some((query) => query.isLoading);
  const data = queries.map((query) => query.data);
  return { data, isLoading };
};

/**
 * Hook to determine the current strategy tab based on the URL
 *
 * @returns {StrategyTab} The current strategy tab
 */
const useCurrentStrategyTab = (): StrategyTab => {
  const location = useLocation();
  const pathname = location.pathname;
  if (pathname.includes('strategy/goals')) {
    return StrategyTab.Goals;
  }
  if (pathname.includes('strategy/outcomes')) {
    return StrategyTab.Outcomes;
  }
  if (pathname.includes('strategy/epics')) {
    return StrategyTab.Epics;
  }
  if (pathname.includes('strategy/mind-map')) {
    return StrategyTab.MindMap;
  }
  if (pathname.includes('strategy/roadmap')) {
    return StrategyTab.Roadmap;
  }
  return StrategyTab.Initiatives;
};

/**
 * Hook to get the strategy tabs
 *
 * @returns {Object} Object containing the strategy tabs
 * @returns {StrategyTab} value - The value of the strategy tab
 * @returns {string} label - The label of the strategy tab
 * @returns {string} icon - The icon of the strategy tab
 * @returns {() => void} onClick - The function to call when the strategy tab is clicked
 * @returns {boolean} show - Whether to show the strategy tab
 */
const useStrategyTabs = (): {
  value: StrategyTab;
  label: string;
  icon: string;
  onClick: () => void;
  show: boolean;
}[] => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const showCorporateGoals = useFeatureFlag(FeatureFlag.CorporateGoals);
  const showStrategyOverview = useFeatureFlag(FeatureFlag.StrategyOverview);

  return [
    {
      value: StrategyTab.Overview,
      label: i18n.t('strategy.tabs.overview') || 'Overview',
      icon: 'dashboard',
      onClick: () => navigate('/application/strategy/overview'),
      show: showCorporateGoals && showStrategyOverview,
    },
    {
      value: StrategyTab.Goals,
      label: i18n.t('strategy.tabs.goals') || 'Strategic Goals',
      icon: 'flag',
      onClick: () => navigate('/application/strategy/goals'),
      show: showCorporateGoals,
    },
    {
      value: StrategyTab.Outcomes,
      label: i18n.t('strategy.tabs.outcomes') || 'Objectives',
      icon: 'checklist',
      onClick: () => navigate('/application/strategy/outcomes'),
      show: showCorporateGoals,
    },
    {
      value: StrategyTab.Initiatives,
      label: i18n.t('strategy.tabs.initiatives') || 'Initiatives',
      icon: 'event_note',
      onClick: () => navigate('/application/strategy'),
      show: true,
    },
    {
      value: StrategyTab.Epics,
      label: i18n.t('common.epics'),
      icon: 'bolt',
      onClick: () => navigate('/application/strategy/epics'),
      show: true,
    },
    {
      value: StrategyTab.MindMap,
      label: i18n.t('strategy.tabs.mind_map') || 'Mind Map',
      icon: 'account_tree',
      onClick: () => navigate('/application/strategy/mind-map'),
      show: showCorporateGoals,
    },
    {
      value: StrategyTab.Roadmap,
      label: i18n.t('strategy.tabs.roadmap') || 'Roadmap',
      icon: 'timeline',
      onClick: () => navigate('/application/strategy/roadmap'),
      show: showCorporateGoals,
    },
  ];
};

export {
  useCreateGoalMutation,
  useCreateInitiativeMutation,
  useCreateOutcomeMutation,
  useCurrentStrategyTab,
  useEpicsQuery,
  useFilteredInitiatives,
  useFiltersApplied,
  useInitiativeMeasureData,
  useInitiativesQuery,
  useStrategyData,
  useStrategyTabs,
};
